import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjectBySlug, getAdjacentProjects, getLocalizedProjectField } from '../../data/projects';
import { getProjectImage } from '../../utils/projectImages';
import { useLanguage } from '../../context/LanguageContext';
import { getCopy } from '../../data/siteCopy';
import './ProjectPage.css';

const sectionTitleMap = {
  pt: {
    Resumo: 'Resumo',
    Objetivos: 'Objetivos',
    Processo: 'Processo',
    'Produto Final': 'Produto Final',
    Extra: 'Extra',
  },
  en: {
    Resumo: 'Summary',
    Objetivos: 'Objectives',
    Processo: 'Process',
    'Produto Final': 'Final Output',
    Extra: 'Extra',
  },
};

const renderContentBlock = (block, index, project) => {
  if (block.type === 'image') {
    const imageSource = getProjectImage(project, block.fileName || block.label);

    return (
      <div key={`${block.label}-${index}`} className="project-content-media">
        {imageSource ? (
          <img
            className="project-content-image"
            src={imageSource}
            alt={`${project.title} - ${block.label || `imagem ${index + 1}`}`}
          />
        ) : (
          <div className="project-image-placeholder large">
            <span>{block.label || `Imagem ${index + 1}`}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <p key={`${block.content}-${index}`} className="section-text">
      {block.content}
    </p>
  );
};

const ProjectPage = () => {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const { prevProject, nextProject } = getAdjacentProjects(slug);
  const { language } = useLanguage();
  const copy = getCopy(language);
  const hasCustomSections = Array.isArray(project?.contentSections) && project.contentSections.length > 0;

  if (!project) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="project-page">
      {/* Project Header */}
      <section className="project-header">
        <div className="project-header-grid">
          <div className="project-header-left">
            <span className="project-category-label">
              {getLocalizedProjectField(project, language, 'category')}
            </span>
            <h1 className="project-title">{project.title}</h1>
            <p className="project-meta">
              {getLocalizedProjectField(project, language, 'yearLabel') || getLocalizedProjectField(project, language, 'type')}
            </p>
            {Array.isArray(project.technologies) && project.technologies.length > 0 && (
              <div className="project-tech-tags" aria-label={copy.projectPage.technologies}>
                {project.technologies.map((technology) => (
                  <span key={technology} className="project-tech-tag">
                    {technology}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="project-header-right">
            <p className="project-context">{getLocalizedProjectField(project, language, 'context')}</p>
          </div>
        </div>
      </section>

      {/* Project Content */}
      <section className="project-content">
        {hasCustomSections ? (
          project.contentSections.map((section) => (
            <div key={section.title} className="project-section">
              <h2 className="section-heading">
                {sectionTitleMap[language][section.title] || section.title}
              </h2>
              <div className="project-section-content">
                {section.blocks.map((block, index) => renderContentBlock(block, index, project))}
              </div>
            </div>
          ))
        ) : (
          <>
            {/* Challenge Section */}
            <div className="project-section">
              <h2 className="section-heading">{copy.projectPage.summary}</h2>
              <p className="section-text">{project.challenge}</p>
            </div>

            {/* Process Section */}
            {project.process && (
              <div className="project-section">
                <h2 className="section-heading">{copy.projectPage.process}</h2>
                <p className="section-text">{project.process}</p>
                {/* Process Images */}
                <div className="project-images-grid process-images">
                  {project.images.slice(0, 2).map((image, index) => (
                    <div key={index} className="project-image-wrapper">
                      <div className="project-image-placeholder">
                        <span>Imagem {index + 1}</span>
                      </div>
                      {/* Replace with actual image when available:
                      <img src={image} alt={`${project.title} - processo ${index + 1}`} />
                      */}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Result Section */}
            <div className="project-section">
              <h2 className="section-heading">{copy.projectPage.finalOutput}</h2>
              <p className="section-text">{project.result}</p>
              {/* Result Images - Full Width */}
              <div className="project-images-full">
                {project.images.map((image, index) => (
                  <div key={index} className="project-image-full">
                    <div className="project-image-placeholder large">
                      <span>Imagem {index + 1}</span>
                    </div>
                    {/* Replace with actual image when available:
                    <img src={image} alt={`${project.title} - resultado ${index + 1}`} />
                    */}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Video Section (for motion/interactive projects) */}
        {project.videoUrl && (
          <div className="project-section">
            <h2 className="section-heading">{copy.projectPage.video}</h2>
            <div className="project-video">
              <div className="video-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
                <span>Vídeo disponível</span>
                <a 
                  href={project.videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="video-link"
                >
                  Abrir vídeo ↗
                </a>
              </div>
              {/* Replace with actual embed when available:
              <iframe 
                src={project.videoUrl}
                title={project.title}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              ></iframe>
              */}
            </div>
            {project.slug === 'pyrenees' && (
              <p className="section-note">
                {copy.projectPage.notePyrenees}
              </p>
            )}
            {project.slug === 'mae-quero-ser-artista' && (
              <p className="section-note">
                {copy.projectPage.noteMae}
              </p>
            )}
          </div>
        )}

        {/* Collaborators Section */}
        {project.collaborators && project.collaborators.length > 0 && (
          <div className="project-section">
            <h2 className="section-heading">{copy.projectPage.credits}</h2>
            <ul className="credits-list">
              {project.collaborators.map((collaborator, index) => (
                <li key={index}>{collaborator}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Project Navigation */}
      <nav className="project-navigation">
        <div className="project-nav-links">
          {prevProject ? (
            <Link to={`/project/${prevProject.slug}`} className="project-nav-link prev">
              <span className="nav-label">{copy.projectPage.previous}</span>
              <span className="nav-title">{prevProject.title}</span>
            </Link>
          ) : (
            <div className="project-nav-link disabled"></div>
          )}
          
          {nextProject ? (
            <Link to={`/project/${nextProject.slug}`} className="project-nav-link next">
              <span className="nav-label">{copy.projectPage.next}</span>
              <span className="nav-title">{nextProject.title}</span>
            </Link>
          ) : (
            <div className="project-nav-link disabled"></div>
          )}
        </div>
        
        <Link to="/" className="back-to-projects">
          {copy.projectPage.back}
        </Link>
      </nav>
    </main>
  );
};

export default ProjectPage;
